// components/entities/pages/OrganisationPage.tsx
import React, { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { BarChart3, Building2, Coins, CreditCard, Share2, Users, type LucideIcon } from '@/components/ui/lucide-memo';
import { SettingsShell } from '@/components/ui/SettingsShell';
import OrganisationMenu from '@/components/OrganisationMenu';
import { OrganisationChartTab } from './OrganisationChartTab';
import { OrganisationTeamsTab } from './OrganisationTeamsTab';
import { isLegacyTeams2DUser } from '#lib/organisation-teams/legacy-teams-2d-access';
import type { OrganisationInventoryEntityState, OrganisationRuntimeState } from '#types/types';

type OrganisationSection = 'GENERAL' | 'MEMBERS' | 'USAGE' | 'BILLING' | 'CHART' | 'TEAMS_JNN';

interface OrganisationPageProps {
  organisationId: string;
  className?: string;
  hasEntityRowAbove?: boolean;
}

const MENU_VIEW: Partial<Record<OrganisationSection, 'general' | 'people' | 'usage' | 'billing'>> = {
  GENERAL: 'general',
  MEMBERS: 'people',
  USAGE: 'usage',
  BILLING: 'billing',
};

const LABELS: Record<'en' | 'fr', Record<OrganisationSection, string>> = {
  en: { GENERAL: 'General', MEMBERS: 'Members', USAGE: 'Usage', BILLING: 'Billing', CHART: 'Hierarchy', TEAMS_JNN: 'Credits Jnn' },
  fr: { GENERAL: 'Général', MEMBERS: 'Membres', USAGE: 'Utilisation', BILLING: 'Facturation', CHART: 'Hiérarchie', TEAMS_JNN: 'Credits Jnn' },
};

const DESCRIPTIONS: Record<'en' | 'fr', Record<OrganisationSection, (name: string) => string>> = {
  en: {
    GENERAL: (n) => `How ${n} looks and behaves: its logo, its defaults, and the instructions every agent reads.`,
    MEMBERS: (n) => `Everyone in ${n}, the teams they belong to, and the invitations still waiting.`,
    USAGE: (n) => `The credits ${n} has left and how it spends them.`,
    BILLING: () => 'The credit plans on record and the invoices that paid for them.',
    CHART: (n) => `Who reports to whom in ${n}.`,
    TEAMS_JNN: () => 'Team credit budgets.',
  },
  fr: {
    GENERAL: (n) => `L'apparence et le comportement de ${n} : son logo, ses réglages par défaut et les instructions lues par chaque agent.`,
    MEMBERS: (n) => `Tous les membres de ${n}, leurs équipes et les invitations en attente.`,
    USAGE: (n) => `Les crédits restants de ${n} et leur consommation.`,
    BILLING: () => 'Les plans de crédits enregistrés et les factures qui les ont payés.',
    CHART: (n) => `Qui rend compte à qui chez ${n}.`,
    TEAMS_JNN: () => 'Budgets de crédits par équipe.',
  },
};

const ICONS: Record<OrganisationSection, LucideIcon> = {
  GENERAL: Building2,
  MEMBERS: Users,
  USAGE: BarChart3,
  BILLING: CreditCard,
  CHART: Share2,
  TEAMS_JNN: Coins,
};

// THE ORGANISATION AS CHATGPT'S SETTINGS SCREEN (owner, 2026-09-29, with chatgpt.com/settings open:
// 'reproduce it exactly'). The page owns the whole window: a black bar on the left with the way
// back in words, a search pill and the sections as plain rows, and on the right one section at a
// time under a large plain title, a one-line explanation, then its settings in a centred column.
// The sections are the ones the settings dialog already splits OrganisationMenu into.
export const OrganisationPage = ({ organisationId, className }: OrganisationPageProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language) === 'fr' ? 'fr' : 'en';
  const dbUserId = useKernelStore((state) => state.userContext.dbUserId);
  const isFaterAdmin = useKernelStore((state) => state.userContext.isFaterAdmin);
  const canListOrganisations = useKernelStore((state) => Boolean(state.userContext.isFaterAdmin || state.userContext.isOrganisationAdmin));
  const canViewLegacyTeams2D = isLegacyTeams2DUser(dbUserId);
  const organisation = useKernelStore(
    (state) =>
      (state.inventory.Organisation.registry[organisationId] as OrganisationInventoryEntityState | undefined) ?? null
  );
  const runtime = useKernelStore((state) => {
    const draft = state.entityKernelState[`Organisation:${organisationId}`]?.state as { runtime?: OrganisationRuntimeState } | undefined;
    return draft?.runtime;
  });
  const storedTab = runtime?.activeTab ?? organisation?.activeTab ?? organisation?.runtime?.activeTab;
  const search = runtime?.settingsSearch ?? '';

  // Billing is Fater's own (plans and invoices); every section it renders is gated on it.
  const sections = useMemo<OrganisationSection[]>(() => [
    'GENERAL', 'MEMBERS', 'USAGE',
    ...(isFaterAdmin ? ['BILLING' as const] : []),
    'CHART',
    ...(canViewLegacyTeams2D ? ['TEAMS_JNN' as const] : []),
  ], [canViewLegacyTeams2D, isFaterAdmin]);
  const items = useMemo(() => sections.map((id) => ({ id, label: LABELS[lang][id], icon: ICONS[id] })), [lang, sections]);
  const activeSection: OrganisationSection = storedTab === 'MANAGEMENT' || !sections.includes(storedTab as OrganisationSection)
    ? 'GENERAL'
    : storedTab as OrganisationSection;

  const handleSectionChange = useCallback((section: OrganisationSection) => {
    kernel.current?.patchEntityRuntimeState('Organisation', organisationId, { activeTab: section });
  }, [kernel, organisationId]);
  const handleSearchChange = useCallback((value: string) => {
    kernel.current?.patchEntityRuntimeState('Organisation', organisationId, { settingsSearch: value });
  }, [kernel, organisationId]);
  // Admins came from the Organisations list and go back to it; anyone else goes back to the app.
  const handleBack = useCallback(() => {
    if (canListOrganisations) void kernel.current?.focusTableUI('organisations');
    else kernel.current?.clearActiveChatSession();
  }, [canListOrganisations, kernel]);

  useEffect(() => {
    if (!kernel.current || !organisationId) return;
    if (!organisation || organisation.stats === undefined || organisation.plans === undefined || organisation.teams === undefined) {
      void kernel.current.syncEntity('Organisation', organisationId);
    }
  }, [kernel, organisationId, organisation]);

  const menuView = MENU_VIEW[activeSection];
  const name = organisation?.name ?? '';

  return (
    <SettingsShell
      className={className}
      backLabel={canListOrganisations ? 'Organisations' : lang === 'fr' ? "Retour à l'app" : 'Back to app'}
      onBack={handleBack}
      search={search}
      onSearchChange={handleSearchChange}
      groupLabel={name}
      items={items}
      activeId={activeSection}
      onSelect={handleSectionChange}
      lang={lang}
    >
        <div className={clsx('mx-auto flex w-full flex-1 flex-col px-8 pb-16 pt-[22px]', activeSection === 'TEAMS_JNN' ? 'max-w-none' : 'max-w-[852px]')}>
          <h1 className="font-normal text-white" style={{ fontSize: 28, lineHeight: '36px' }}>{LABELS[lang][activeSection]}</h1>
          <p className="mt-[38px] text-[#ECECEC]" style={{ fontSize: 15, lineHeight: '21px' }}>{DESCRIPTIONS[lang][activeSection](name)}</p>
          <div className="mt-8 flex min-h-0 flex-1 flex-col">
            {menuView ? (
              <OrganisationMenu isActive targetOrgId={organisationId} settingsView={menuView} />
            ) : activeSection === 'CHART' ? (
              <OrganisationChartTab organisationId={organisationId} />
            ) : (
              <OrganisationTeamsTab organisationId={organisationId} renderLegacy2D />
            )}
          </div>
        </div>
    </SettingsShell>
  );
};
